/**
 * AnalyticsDashboard
 *
 * Phase 1 analytics dashboard:
 *   - MetricCards: 5 funnel cards always visible (Connected → Signed up)
 *   - Testers tab: Per-tester table with sortable columns + drill-down
 *   - Activity tab: Exchange feed with mobile card layout
 *
 * Access at: /admin/dashboard
 * Demo mode: /admin/dashboard?demo=true
 *
 * Data sources:
 *   - analytics_events table (funnel events, app_open, connection_location, manual_add)
 *   - chat_messages table (chat counts, photo counts, invite flags)
 *   - connection_data table (contact types, guest names, notes — source of truth)
 *   - connections table (host_user_id — identity-based host/guest resolution)
 *   - profiles table (tester names, phones, timestamps)
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { Trash } from '@phosphor-icons/react/dist/ssr/Trash';
import { CheckSquare } from '@phosphor-icons/react/dist/ssr/CheckSquare';
import { Square } from '@phosphor-icons/react/dist/ssr/Square';
import { Gift as GiftIcon } from '@phosphor-icons/react/dist/ssr/Gift';
import { Phone as PhoneIcon } from '@phosphor-icons/react/dist/ssr/Phone';
import { EnvelopeSimple } from '@phosphor-icons/react/dist/ssr/EnvelopeSimple';
import { InstagramLogo } from '@phosphor-icons/react/dist/ssr/InstagramLogo';
import { LinkedinLogo } from '@phosphor-icons/react/dist/ssr/LinkedinLogo';
import { WhatsappLogo } from '@phosphor-icons/react/dist/ssr/WhatsappLogo';
import { FacebookLogo } from '@phosphor-icons/react/dist/ssr/FacebookLogo';
import { GithubLogo } from '@phosphor-icons/react/dist/ssr/GithubLogo';
import { TelegramLogo } from '@phosphor-icons/react/dist/ssr/TelegramLogo';
import { XLogo } from '@phosphor-icons/react/dist/ssr/XLogo';
import { YoutubeLogo } from '@phosphor-icons/react/dist/ssr/YoutubeLogo';
import { User as UserIcon } from '@phosphor-icons/react/dist/ssr/User';
import { Camera } from '@phosphor-icons/react/dist/ssr/Camera';
import { Note as NoteIcon } from '@phosphor-icons/react/dist/ssr/Note';
import { FileArrowDown } from '@phosphor-icons/react/dist/ssr/FileArrowDown';
import { DotsThree } from '@phosphor-icons/react/dist/ssr/DotsThree';
import { ChatCircle } from '@phosphor-icons/react/dist/ssr/ChatCircle';
import { COLORS, SPACING, TYPOGRAPHY, FONT_FAMILY } from '../../styles/UISystem';
import { supabase } from '../../services/supabase';
import { mapAnalyticsEvent } from '../../services/analytics/eventLogger';

// ============================================================
// CONSTANTS
// ============================================================

const TIME_FILTERS = {
  today: { label: 'Today', days: 0 },
  '7days': { label: 'Last 7 days', days: 7 },
  '30days': { label: 'Last 30 days', days: 30 },
  all: { label: 'All time', days: null },
};

// Event types that create tester rows — error/recovery/location events are excluded
const TESTER_EVENT_TYPES = new Set([
  'funnel_qr_scanned',
  'funnel_contact_entered',
  'funnel_contact_saved',
  'funnel_signup_waitlist',
  'funnel_signup_converted',
  'manual_add',
  'app_open',
]);

// Event type → display label mapping for Dashboard MetricCards
const FUNNEL_METRICS = [
  { eventType: 'funnel_qr_scanned', label: 'Connected', isBaseline: true, role: 'profile' },
  { eventType: 'funnel_contact_entered', label: 'Shared contact', role: 'anon' },
  { eventType: 'funnel_contact_saved', label: 'Saved contact', role: 'anon' },
  { eventType: 'funnel_invite_sent', label: 'Invite sent', role: 'profile' },
  { eventType: 'funnel_signup_waitlist', label: 'Signed up', role: 'anon' },
  // "Signed up" = waitlist + converted (completed flows only). Confirmed alone doesn't count.
];

// ============================================================
// DEMO DATA
// ============================================================

const DEMO_EVENTS = (() => {
  const now = new Date();
  const ago = (days) => new Date(now - days * 86400000).toISOString();
  const testers = ['Mark Mangan', 'Sarah Chen', 'Alex Rivera'];
  const events = [];
  let id = 0;

  testers.forEach((name, ti) => {
    // Each tester gets some connections
    const connectCount = 5 + ti * 3;
    for (let c = 0; c < connectCount; c++) {
      const chatRoomId = `demo_chat_${ti}_${c}`;
      const day = ago(Math.floor(Math.random() * 14));

      events.push({
        id: `demo-${id++}`,
        event_type: 'funnel_qr_scanned',
        event_data: { chatRoomId, profileUsername: name },
        branch: 'main',
        created_at: day,
      });

      if (Math.random() > 0.2) {
        events.push({
          id: `demo-${id++}`,
          event_type: 'funnel_contact_entered',
          event_data: {
            chatRoomId,
            contactTypes: ['phone'],
            anonName: `Anon ${c}`,
          },
          branch: 'main',
          created_at: day,
        });
      }

      if (Math.random() > 0.3) {
        events.push({
          id: `demo-${id++}`,
          event_type: 'funnel_contact_saved',
          event_data: { chatRoomId },
          branch: 'main',
          created_at: day,
        });
      }

      if (Math.random() > 0.5) {
        events.push({
          id: `demo-${id++}`,
          event_type: 'funnel_invite_sent',
          event_data: { chatRoomId, profileUsername: name },
          branch: 'main',
          created_at: day,
        });

        if (Math.random() > 0.3) {
          events.push({
            id: `demo-${id++}`,
            event_type: 'funnel_invite_clicked',
            event_data: { chatRoomId, canCreateProfile: ti === 0 },
            branch: 'main',
            created_at: day,
          });
        }
      }

      if (Math.random() > 0.6) {
        events.push({
          id: `demo-${id++}`,
          event_type: 'funnel_signup_waitlist',
          event_data: {
            chatRoomId,
            connectedWith: name,
            source: Math.random() > 0.5 ? 'invite_cta' : 'connections_list',
          },
          branch: 'main',
          created_at: day,
        });
      }

      if (Math.random() > 0.5) {
        events.push({
          id: `demo-${id++}`,
          event_type: 'funnel_profile_shared',
          event_data: { chatRoomId, contactTypes: ['phone', 'instagram'] },
          branch: 'main',
          created_at: day,
        });
      }
    }

    // Browse events
    for (let b = 0; b < 3 + ti; b++) {
      events.push({
        id: `demo-${id++}`,
        event_type: 'app_open',
        event_data: { profileUsername: name },
        branch: 'main',
        created_at: ago(Math.floor(Math.random() * 7)),
      });
    }

    // Manual adds
    if (ti === 0) {
      events.push({
        id: `demo-${id++}`,
        event_type: 'manual_add',
        event_data: {
          chatRoomId: `demo_manual_${ti}`,
          contactName: 'Quick Add',
          profileUsername: name,
        },
        branch: 'main',
        created_at: ago(2),
      });
    }
  });

  return events;
})();

// Demo chat messages for Testers/Activity views
const DEMO_CHAT_MESSAGES = (() => {
  const messages = [];
  // Add some chat messages for demo connections
  for (let ti = 0; ti < 3; ti++) {
    for (let c = 0; c < 3; c++) {
      const chatRoomId = `demo_chat_${ti}_${c}`;
      messages.push({ chat_room_id: chatRoomId, sender: 'profile', content: {} });
      if (Math.random() > 0.4) {
        messages.push({ chat_room_id: chatRoomId, sender: 'anon', content: {} });
      }
    }
    // One connection with an invite CTA
    messages.push({
      chat_room_id: `demo_chat_${ti}_0`,
      sender: 'profile',
      content: { isInviteCta: true },
    });
  }
  return messages;
})();

// ============================================================
// HELPERS
// ============================================================

/**
 * Paginated fetch — loops until all rows are retrieved.
 * PostgREST silently truncates at 1000 rows; this prevents that.
 */
async function fetchAllRows(queryBuilder, pageSize = 1000) {
  let allData = [];
  let from = 0;
  let hasMore = true;
  while (hasMore) {
    const { data, error } = await queryBuilder.range(from, from + pageSize - 1);
    if (error) throw error;
    allData = allData.concat(data || []);
    hasMore = data && data.length === pageSize;
    from += pageSize;
  }
  return allData;
}

// ============================================================
// COMPONENT
// ============================================================

const AnalyticsDashboard = () => {
  // --- State ---
  const [activeView, setActiveView] = useState('activity');
  const [branchFilter, setBranchFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [testerFilter, setTesterFilter] = useState(null); // for Activity drill-down
  const [sortConfig, setSortConfig] = useState({ key: 'last', direction: 'desc' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activityLimit, setActivityLimit] = useState(100);
  const [activityTypeFilter, setActivityTypeFilter] = useState('all'); // 'all' | 'signups'
  const [pendingDeleteId, setPendingDeleteId] = useState(null); // inline confirm for pending delete
  const [testerDeleteId, setTesterDeleteId] = useState(null); // inline confirm for tester delete
  const [timingRows, setTimingRows] = useState([]);
  const [timingLoading, setTimingLoading] = useState(false);
  const [profilePhoneMap, setProfilePhoneMap] = useState({}); // profileUserId → phone
  const [profileDataList, setProfileDataList] = useState([]); // [{profileUserId, name}] from DB
  const [profileJoinedMap, setProfileJoinedMap] = useState({}); // profileUserId → profiles.created_at
  const [profileLastMap, setProfileLastMap] = useState({}); // profileUserId → profiles.updated_at
  const [nameToProfileId, setNameToProfileId] = useState({}); // name → profileUserId (historical event resolution)
  const [pendingProfiles, setPendingProfiles] = useState([]); // pending profile records
  const [connectionDataMap, setConnectionDataMap] = useState({}); // chatRoomId → { [profile_user_id]: row }
  const [chatRoomToHost, setChatRoomToHost] = useState({}); // chatRoomId → host_user_id (from connections)

  // --- Demo mode check ---
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('demo') === 'true') {
      setDemoMode(true);
      setEvents(DEMO_EVENTS);
      setChatMessages(DEMO_CHAT_MESSAGES);
      setLoading(false);
    }
  }, []);

  // --- Data fetching ---
  const fetchEvents = useCallback(async () => {
    if (demoMode) {
      setEvents(DEMO_EVENTS);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('analytics_events')
        .select('*')
        .order('created_at', { ascending: false });

      if (timeFilter !== 'all') {
        const filterConfig = TIME_FILTERS[timeFilter];
        if (filterConfig && filterConfig.days !== null) {
          const startDate = new Date();
          if (filterConfig.days === 0) {
            startDate.setHours(0, 0, 0, 0);
          } else {
            startDate.setDate(startDate.getDate() - filterConfig.days);
          }
          query = query.gte('created_at', startDate.toISOString());
        }
      }

      const data = await fetchAllRows(query);
      setEvents(data.map(mapAnalyticsEvent));
    } catch (err) {
      console.error('[Dashboard] Fetch error:', err);
      setError(err.message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [timeFilter, demoMode]);

  const fetchChatMessages = useCallback(
    async (chatRoomIds) => {
      if (demoMode) {
        setChatMessages(DEMO_CHAT_MESSAGES);
        return;
      }

      if (!chatRoomIds || chatRoomIds.length === 0) {
        setChatMessages([]);
        return;
      }

      try {
        // Fetch chat messages for known chatRoomIds
        // chat type → purple dots, photo counts, invite CTA detection
        // sender_id used for identity-based attribution (sender field is unreliable after conversion)
        // Guest name + contact types now come from connection_data (guestDataMap), not chat_messages
        const chats = await fetchAllRows(
          supabase
            .from('chat_messages')
            .select('chat_room_id, sender, sender_id, type, content')
            .eq('type', 'chat')
            .in('chat_room_id', chatRoomIds)
        );

        setChatMessages(chats);
      } catch (err) {
        console.error('[Dashboard] Chat fetch exception:', err);
      }
    },
    [demoMode]
  );

  const fetchErrorCount = useCallback(async () => {
    if (demoMode) return;

    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const { count, error: countError } = await supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('event_type', 'error')
        .gte('created_at', sevenDaysAgo.toISOString());

      if (!countError) {
        setErrorCount(count || 0);
      }
    } catch (err) {
      console.error('[Dashboard] Error count fetch failed:', err);
    }
  }, [demoMode]);

  const fetchConnectionData = useCallback(async () => {
    if (demoMode) return;
    try {
      const { data, error: fetchErr } = await supabase
        .from('connection_data')
        .select(
          'chat_room_id, profile_user_id, notes, sent_shared_fields, received_contact, manual_contact, name_sharing'
        );

      if (fetchErr) {
        console.error('[Dashboard] connection_data fetch error:', fetchErr);
        return;
      }

      // chatRoomId → { [profile_user_id]: row }
      const map = {};
      (data || []).forEach((row) => {
        if (!row.chat_room_id) return;
        if (!map[row.chat_room_id]) map[row.chat_room_id] = {};
        map[row.chat_room_id][row.profile_user_id] = row;
      });
      setConnectionDataMap(map);
    } catch (err) {
      console.error('[Dashboard] connection_data fetch failed:', err);
    }
  }, [demoMode]);

  const fetchConnectionHosts = useCallback(async () => {
    if (demoMode) return;
    try {
      const { data, error: fetchErr } = await supabase
        .from('connections')
        .select('chat_room_id, host_user_id');
      if (fetchErr) {
        console.error('[Dashboard] Connections fetch error:', fetchErr);
        return;
      }
      const map = {};
      (data || []).forEach((row) => {
        if (row.chat_room_id && row.host_user_id) {
          map[row.chat_room_id] = row.host_user_id;
        }
      });
      setChatRoomToHost(map);
    } catch (err) {
      console.error('[Dashboard] Connections fetch failed:', err);
    }
  }, [demoMode]);

  const fetchTimingData = useCallback(async () => {
    if (demoMode) {
      setTimingRows([]);
      return;
    }
    setTimingLoading(true);
    try {
      const { data, error: fetchErr } = await supabase
        .from('connection_timings')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      if (!fetchErr) {
        setTimingRows(data || []);
      }
    } catch (err) {
      console.error('[Dashboard] Timing fetch failed:', err);
    } finally {
      setTimingLoading(false);
    }
  }, [demoMode]);

  // Fetch profile data for tester table (phone, created_at, updated_at)
  // Keyed by profile_user_id — each profile is its own row regardless of name
  const fetchProfilePhones = useCallback(async () => {
    if (demoMode) return;
    try {
      const { data } = await supabase
        .from('profiles')
        .select('profile_user_id, name, phone, created_at, updated_at, last_active_at')
        .order('created_at', { ascending: false });
      if (data) {
        const phoneMap = {};
        const joinedMap = {};
        const lastMap = {};
        const profiles = [];
        const nameMap = {};
        data.forEach((p) => {
          if (!p.profile_user_id) return;
          profiles.push({
            profileUserId: p.profile_user_id,
            name: p.name,
            lastActiveAt: p.last_active_at,
          });
          if (p.phone) phoneMap[p.profile_user_id] = p.phone;
          if (p.created_at) joinedMap[p.profile_user_id] = p.created_at;
          if (p.updated_at) lastMap[p.profile_user_id] = p.updated_at;
          // For historical event resolution — map name to profileUserId
          // If two profiles share a name, set null so we don't misattribute
          if (p.name) {
            if (!(p.name in nameMap)) {
              nameMap[p.name] = p.profile_user_id;
            } else if (nameMap[p.name] !== p.profile_user_id) {
              nameMap[p.name] = null;
            }
          }
        });
        setProfilePhoneMap(phoneMap);
        setProfileDataList(profiles);
        setProfileJoinedMap(joinedMap);
        setProfileLastMap(lastMap);
        setNameToProfileId(nameMap);
      }
    } catch (err) {
      console.error('[Dashboard] Profile phone fetch failed:', err);
    }
  }, [demoMode]);

  // Fetch pending profiles for the Pending Profiles list on Testers tab
  // Enriches each record with Anon name (from chat_messages) and Profile user name (from connections → profiles)
  const fetchPendingProfiles = useCallback(async () => {
    if (demoMode) return;
    try {
      const { data, error: fetchErr } = await supabase
        .from('pending_profiles')
        .select('id, phone, profile_user_id, anon_user_id, created_at, expires_at')
        .order('created_at', { ascending: false });
      if (fetchErr) {
        console.error('[Dashboard] Pending profiles fetch failed:', fetchErr);
        return;
      }

      // Enrich with names
      const enriched = await Promise.all(
        (data || []).map(async (p) => {
          let anonName = null;
          let profileUserName = null;

          // Get Anon's name from chat_messages (anonName in content)
          if (p.anon_user_id) {
            const { data: msg } = await supabase
              .from('chat_messages')
              .select('content')
              .eq('sender_id', p.anon_user_id)
              .not('content->>anonName', 'is', null)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            anonName = msg?.content?.anonName || null;
          }

          // Get Profile user's name via connections → profiles
          if (p.anon_user_id) {
            const { data: conn } = await supabase
              .from('connections')
              .select('host_user_id')
              .eq('guest_user_id', p.anon_user_id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            if (conn?.host_user_id) {
              const { data: profile } = await supabase
                .from('profiles')
                .select('name')
                .eq('profile_user_id', conn.host_user_id)
                .maybeSingle();
              profileUserName = profile?.name || null;
            }
          }

          return { ...p, anonName, profileUserName };
        })
      );

      setPendingProfiles(enriched);
    } catch (err) {
      console.error('[Dashboard] Pending profiles fetch failed:', err);
    }
  }, [demoMode]);

  // Delete a pending profile record + its synced connection_data
  const handleDeletePending = async (pendingRecord) => {
    if (demoMode) return;

    // First click: show inline "Confirm?" state
    if (pendingDeleteId !== pendingRecord.id) {
      setPendingDeleteId(pendingRecord.id);
      // Auto-reset after 3s if not confirmed
      setTimeout(
        () => setPendingDeleteId((prev) => (prev === pendingRecord.id ? null : prev)),
        3000
      );
      return;
    }

    // Second click: confirmed — execute delete
    setPendingDeleteId(null);

    try {
      if (pendingRecord.profile_user_id) {
        const { error: cdErr } = await supabase.rpc('dashboard_delete_connection_data', {
          p_profile_user_id: pendingRecord.profile_user_id,
        });
        if (cdErr) console.error('[Dashboard] connection_data delete error:', cdErr);
      }

      const { error: ppErr } = await supabase
        .from('pending_profiles')
        .delete()
        .eq('id', pendingRecord.id);

      if (ppErr) {
        console.error('[Dashboard] pending_profiles delete error:', ppErr);
        return;
      }

      fetchPendingProfiles();
    } catch (err) {
      console.error('[Dashboard] Delete pending profile failed:', err);
    }
  };

  // Fetch on mount and when filters change
  // Events must load first so we can scope chat query to known chatRoomIds
  useEffect(() => {
    const loadData = async () => {
      await fetchEvents();
      fetchErrorCount();
      fetchProfilePhones();
      fetchPendingProfiles();
      fetchConnectionData();
      fetchConnectionHosts();
    };
    loadData();
  }, [
    fetchEvents,
    fetchErrorCount,
    fetchProfilePhones,
    fetchPendingProfiles,
    fetchConnectionData,
    fetchConnectionHosts,
  ]);

  // Fetch timing data when timing view is selected
  useEffect(() => {
    if (activeView === 'timing') {
      fetchTimingData();
    }
  }, [activeView, fetchTimingData]);

  // Fetch chat messages when events change (scoped to known chatRoomIds)
  useEffect(() => {
    const chatRoomIds = [...new Set(events.map((e) => e.event_data?.chatRoomId).filter(Boolean))];
    fetchChatMessages(chatRoomIds);
  }, [events, fetchChatMessages]);

  // --- Orphan cleanup on mount ---
  useEffect(() => {
    const cleanOrphanEvents = async () => {
      if (demoMode) return;

      try {
        const validScans = await fetchAllRows(
          supabase
            .from('analytics_events')
            .select('id, metadata->chatRoomId')
            .eq('event_type', 'funnel')
            .eq('event_name', 'qr_scanned')
        );

        const validChatRoomIds = new Set(
          validScans.map((e) => e.chatRoomId).filter((id) => id != null)
        );

        const allFunnelEvents = await fetchAllRows(
          supabase
            .from('analytics_events')
            .select('id, event_type, event_name, metadata->chatRoomId')
            .eq('event_type', 'funnel')
        );

        const orphanEvents = allFunnelEvents.filter((e) => {
          const chatRoomId = e.chatRoomId;
          return chatRoomId && !validChatRoomIds.has(chatRoomId);
        });

        if (orphanEvents.length === 0) return;

        const orphanIds = orphanEvents.map((e) => e.id);
        console.log(`[Dashboard] Cleaning ${orphanEvents.length} orphan events`);

        await supabase.from('analytics_events').delete().in('id', orphanIds);
        fetchEvents();
      } catch (err) {
        console.error('[Dashboard] Orphan cleanup error:', err);
      }
    };

    cleanOrphanEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Computed data ---
  const availableBranches = useMemo(() => {
    const branches = new Set(events.map((e) => e.branch).filter(Boolean));
    branches.add('main'); // Always show main as an option
    return ['all', ...[...branches].sort()];
  }, [events]);

  const filteredEvents = useMemo(() => {
    if (branchFilter === 'all') return events;
    return events.filter((e) => e.branch === branchFilter);
  }, [events, branchFilter]);

  // Chat message maps: { chatRoomId → { senderId: count } }
  // Chat/photo counts keyed by sender_id (not sender role).
  // The sender field ('profile'/'anon') is unreliable after anon→profile conversion —
  // converted guests send with sender='profile' but are still the guest.
  // Row builder resolves host vs guest using chatRoomToProfile.
  const chatCountMap = useMemo(() => {
    const map = {};
    chatMessages.forEach((msg) => {
      if (msg.type !== 'chat') return;
      // Profile side uses isFreeform flag; Anon side doesn't set it,
      // so exclude auto-name messages (have anonName) instead
      if (msg.sender === 'profile' && !msg.content?.isFreeform) return;
      if (msg.sender === 'anon' && msg.content?.anonName) return;
      const rid = msg.chat_room_id;
      const sid = msg.sender_id || msg.sender;
      if (!map[rid]) map[rid] = {};
      map[rid][sid] = (map[rid][sid] || 0) + 1;
    });
    return map;
  }, [chatMessages]);

  // Photo map: chatRoomIds where image messages were sent, by sender_id
  const photoMap = useMemo(() => {
    const map = {};
    chatMessages.forEach((msg) => {
      if (msg.type !== 'chat' || msg.content?.contentType !== 'image') return;
      const rid = msg.chat_room_id;
      const sid = msg.sender_id || msg.sender;
      if (!map[rid]) map[rid] = {};
      map[rid][sid] = (map[rid][sid] || 0) + 1;
    });
    return map;
  }, [chatMessages]);

  // Invite set: chatRoomIds that have isInviteCta messages
  const inviteSet = useMemo(() => {
    const set = new Set();
    chatMessages.forEach((msg) => {
      if (msg.content?.isInviteCta) {
        set.add(msg.chat_room_id);
      }
    });
    return set;
  }, [chatMessages]);

  // Notes map derived from connectionDataMap — chatRoomId → Set of profile_user_ids who wrote notes
  const notesMap = useMemo(() => {
    const map = {};
    Object.entries(connectionDataMap).forEach(([chatRoomId, rows]) => {
      Object.entries(rows).forEach(([profileUserId, row]) => {
        if (row.notes) {
          if (!map[chatRoomId]) map[chatRoomId] = new Set();
          map[chatRoomId].add(profileUserId);
        }
      });
    });
    return map;
  }, [connectionDataMap]);

  // chatRoomId → profile_user_id lookup (used by tester rows + activity filter)
  // New events: use profile_user_id column directly
  // Historical events (both columns null): resolve name → profileUserId via nameToProfileId
  const chatRoomToProfile = useMemo(() => {
    // Base: resolve from analytics_events (covers manual adds + orphaned events without connections rows)
    const map = {};
    filteredEvents.forEach((e) => {
      const chatRoomId = e.event_data?.chatRoomId;
      if (!chatRoomId) return;

      if (e.profile_user_id) {
        map[chatRoomId] = e.profile_user_id;
      } else if (
        !e.profile_user_id &&
        !e.anon_user_id &&
        e.event_data?.profileUsername &&
        TESTER_EVENT_TYPES.has(e.event_type)
      ) {
        const resolved = nameToProfileId[e.event_data.profileUsername];
        if (resolved) map[chatRoomId] = resolved;
      }
    });
    // Override: connections.host_user_id is the definitive host.
    // analytics_events can resolve to the guest after anon→profile conversion
    // (converted guest writes events with their own profile_user_id).
    Object.entries(chatRoomToHost).forEach(([chatRoomId, hostId]) => {
      map[chatRoomId] = hostId;
    });
    return map;
  }, [filteredEvents, nameToProfileId, chatRoomToHost]);

  // Guest data derived from connection_data (source of truth).
  // Host's received_contact contains guest name + shared fields — works for all connection types
  // (anon↔profile, converted guests, PXP, manual adds).
  const guestDataMap = useMemo(() => {
    const MANUAL_CONTACT_FIELDS = [
      'phone',
      'email',
      'instagram',
      'whatsapp',
      'linkedin',
      'telegram',
      'x',
    ];
    const map = {};
    Object.entries(connectionDataMap).forEach(([chatRoomId, rows]) => {
      const hostId = chatRoomToProfile[chatRoomId];
      if (!hostId) return;
      const hostRow = rows[hostId];
      if (!hostRow) return;

      // Manual add: derive from manual_contact fields
      if (hostRow.manual_contact) {
        const mc = hostRow.manual_contact;
        const types = MANUAL_CONTACT_FIELDS.filter((f) => mc[f]);
        map[chatRoomId] = {
          name: mc.name || null,
          contactTypes: types.length > 0 ? types : null,
        };
        return;
      }

      // Standard connection: guest data from host's received_contact
      const rc = hostRow.received_contact;
      if (!rc) return;
      const sharedFields = rc.sharedFields;
      const types = sharedFields
        ? sharedFields.filter((f) => f !== 'photoUrl' && f !== 'name')
        : null;
      map[chatRoomId] = {
        name: rc.name || null,
        contactTypes: types && types.length > 0 ? types : null,
      };
    });
    return map;
  }, [connectionDataMap, chatRoomToProfile]);

  // --- Dashboard metrics ---
  const dashboardMetrics = useMemo(() => {
    const counts = {};
    FUNNEL_METRICS.forEach((m) => {
      counts[m.eventType] = 0;
    });

    // Dedup set for events that can fire multiple times per connection
    const savedChatRoomIds = new Set();
    filteredEvents.forEach((e) => {
      if (counts[e.event_type] !== undefined) {
        // Deduplicate contact_saved by chatRoomId (Anon can tap Save multiple times)
        if (e.event_type === 'funnel_contact_saved') {
          const crid = e.event_data?.chatRoomId;
          if (crid && savedChatRoomIds.has(crid)) return;
          if (crid) savedChatRoomIds.add(crid);
        }
        counts[e.event_type]++;
      }
    });

    // Shared contact: derive from connection_data (guest shared fields)
    // funnel_contact_entered REST event is unreliable from Anon side
    counts['funnel_contact_entered'] = Object.values(guestDataMap).filter(
      (g) => g.contactTypes
    ).length;

    // "Signed up" = completed flows only (waitlist + full PWA conversion)
    // Phone confirmation alone (funnel_signup_confirmed) is just step 1 — doesn't count
    const waitlistChatRoomIds = new Set();
    const convertedChatRoomIds = new Set();
    filteredEvents.forEach((e) => {
      if (e.event_type === 'funnel_signup_waitlist' && e.event_data?.chatRoomId) {
        waitlistChatRoomIds.add(e.event_data.chatRoomId);
      }
      if (e.event_type === 'funnel_signup_converted' && e.event_data?.chatRoomId) {
        convertedChatRoomIds.add(e.event_data.chatRoomId);
      }
    });
    // Union of both sets — don't double-count users who did waitlist then converted
    const allSignupChatRoomIds = new Set([...waitlistChatRoomIds, ...convertedChatRoomIds]);
    counts['funnel_signup_waitlist'] = allSignupChatRoomIds.size;
    // Breakdown: "waitlist only" = in waitlist but NOT converted, "converted" = completed full conversion
    const waitlistOnlyCount = [...waitlistChatRoomIds].filter(
      (id) => !convertedChatRoomIds.has(id)
    ).length;
    // Welcome page signups (no chatRoomId — standalone, not part of funnel)
    const welcomeSignupCount = filteredEvents.filter(
      (e) => e.event_type === 'funnel_signup_waitlist' && !e.event_data?.chatRoomId
    ).length;

    const baseline = counts['funnel_qr_scanned'] || 0;

    return FUNNEL_METRICS.map((m) => {
      const count = counts[m.eventType];
      if (m.isBaseline) {
        return { ...m, count, display: String(count), suffix: '' };
      }
      const pct = baseline > 0 ? Math.round((count / baseline) * 100) : '0';
      const result = { ...m, count, display: pct, suffix: '%' };
      // Attach breakdown for the "Signed up" card
      if (m.eventType === 'funnel_signup_waitlist') {
        result.waitlistCount = waitlistOnlyCount;
        result.convertedCount = convertedChatRoomIds.size;
        result.welcomeCount = welcomeSignupCount;
      }
      return result;
    });
  }, [filteredEvents, guestDataMap]);

  // --- Tester rows (Step 3) ---
  // Keyed by profile_user_id — each profile is its own row regardless of name
  const testerRows = useMemo(() => {
    const testerChatRooms = {};
    const testerEvents = {};
    const testerNameMap = {}; // profileUserId → name (for display)

    filteredEvents.forEach((e) => {
      if (!TESTER_EVENT_TYPES.has(e.event_type)) return;

      const data = e.event_data || {};

      // Resolve event → profile_user_id
      let profileUserId = null;

      if (e.profile_user_id) {
        // New events: identity column
        profileUserId = e.profile_user_id;
      } else if (e.anon_user_id || (!e.profile_user_id && !data.profileUsername)) {
        // Anon-side or unattributable: resolve via chatRoomToProfile
        profileUserId = chatRoomToProfile[data.chatRoomId] || null;
      } else if (data.profileUsername) {
        // Historical events: resolve name → profileUserId
        profileUserId = nameToProfileId[data.profileUsername] || null;
      }

      // Signup events: resolve via chatRoomToProfile to attribute to the QR owner
      // Skip if no chatRoomId — DemoGate profile creation, not a funnel conversion
      if (e.event_type === 'funnel_signup_waitlist' || e.event_type === 'funnel_signup_converted') {
        if (!data.chatRoomId) return;
        profileUserId = chatRoomToProfile[data.chatRoomId] || profileUserId;
      }

      if (!profileUserId) return;

      // Track name for display
      if (data.profileUsername && !testerNameMap[profileUserId]) {
        testerNameMap[profileUserId] = data.profileUsername;
      }

      if (!testerEvents[profileUserId]) {
        testerEvents[profileUserId] = {
          qrScanned: new Set(),
          contactEntered: new Set(),
          contactSaved: new Set(),
          signups: 0,
          manualAdds: 0,
          appOpens: 0,
          lastOpenedTimestamp: null,
          lastTimestamp: null,
          firstTimestamp: null,
          branch: null,
        };
      }

      const t = testerEvents[profileUserId];
      const ts = e.created_at;
      // Track branch from earliest event
      if (e.branch && (!t.branch || (ts && t.firstTimestamp && ts < t.firstTimestamp))) {
        t.branch = e.branch;
      }
      if (!t.lastTimestamp || ts > t.lastTimestamp) t.lastTimestamp = ts;

      const chatRoomId = data.chatRoomId;

      switch (e.event_type) {
        case 'funnel_qr_scanned':
          t.qrScanned.add(chatRoomId);
          if (!testerChatRooms[profileUserId]) testerChatRooms[profileUserId] = new Set();
          testerChatRooms[profileUserId].add(chatRoomId);
          if (!t.firstTimestamp || ts < t.firstTimestamp) t.firstTimestamp = ts;
          break;
        case 'funnel_contact_entered':
          t.contactEntered.add(chatRoomId);
          break;
        case 'funnel_contact_saved':
          t.contactSaved.add(chatRoomId);
          break;
        case 'funnel_signup_waitlist':
          t.signups++;
          break;
        case 'funnel_signup_converted':
          t.conversions = (t.conversions || 0) + 1;
          if (!t.firstTimestamp || ts < t.firstTimestamp) t.firstTimestamp = ts;
          break;
        case 'manual_add':
          t.manualAdds++;
          break;
        case 'app_open':
          t.appOpens++;
          if (!t.lastOpenedTimestamp || ts > t.lastOpenedTimestamp) t.lastOpenedTimestamp = ts;
          break;
        default:
          break;
      }
    });

    // Name lookup: prefer profiles query, fall back to event-derived name
    const getName = (pid) => {
      const fromDb = profileDataList.find((p) => p.profileUserId === pid);
      return fromDb?.name || testerNameMap[pid] || pid;
    };

    // Build rows from events
    const eventRows = Object.entries(testerEvents).map(([profileUserId, t]) => {
      const connects = t.qrScanned.size;
      const chatRoomIds = testerChatRooms[profileUserId] || new Set();

      // Chat %: chatRoomIds with any chat messages / connects
      let chatCount = 0;
      let invitedCount = 0;
      // Shared: from chat_messages (funnel_contact_entered has 0 entries)
      // Saved: from analytics_events only (funnel_contact_saved now fires correctly)
      let sharedCount = 0;
      let savedCount = t.contactSaved.size;
      chatRoomIds.forEach((rid) => {
        const cc = chatCountMap[rid];
        if (cc && Object.values(cc).some((v) => v > 0)) chatCount++;
        if (inviteSet.has(rid)) invitedCount++;
        const guestData = guestDataMap[rid];
        if (guestData?.contactTypes) sharedCount++;
      });

      return {
        profileUserId,
        name: getName(profileUserId),
        phone: profilePhoneMap[profileUserId] || null,
        last: t.lastTimestamp || profileLastMap[profileUserId] || null,
        lastOpened:
          profileDataList.find((p) => p.profileUserId === profileUserId)?.lastActiveAt || null,
        connects,
        sharedPct: connects > 0 ? Math.round((sharedCount / connects) * 100) : 0,
        savedPct: connects > 0 ? Math.round((savedCount / connects) * 100) : 0,
        chatPct: connects > 0 ? Math.round((chatCount / connects) * 100) : 0,
        invited: invitedCount,
        signups: t.signups,
        conversions: t.conversions || 0,
        manual: t.manualAdds,
        browsed: t.appOpens,
        joined: t.firstTimestamp || profileJoinedMap[profileUserId] || null,
        branch: t.branch || null,
      };
    });
    // Add profiles that have no events (registered but no activity)
    const eventIds = new Set(eventRows.map((r) => r.profileUserId));
    const profileOnlyRows = profileDataList
      .filter((p) => !eventIds.has(p.profileUserId))
      .map((p) => ({
        profileUserId: p.profileUserId,
        name: p.name,
        phone: profilePhoneMap[p.profileUserId] || null,
        last: profileLastMap[p.profileUserId] || null,
        lastOpened: p.lastActiveAt || null,
        connects: 0,
        sharedPct: 0,
        savedPct: 0,
        chatPct: 0,
        invited: 0,
        signups: 0,
        conversions: 0,
        manual: 0,
        browsed: 0,
        joined: profileJoinedMap[p.profileUserId] || null,
      }));

    return [...eventRows, ...profileOnlyRows];
  }, [
    filteredEvents,
    chatRoomToProfile,
    chatCountMap,
    inviteSet,
    guestDataMap,
    profilePhoneMap,
    profileDataList,
    profileJoinedMap,
    profileLastMap,
    nameToProfileId,
  ]);

  // Sorted tester rows
  const sortedTesterRows = useMemo(() => {
    const rows = [...testerRows];
    rows.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // String comparison for name
      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      // Date comparison
      if (sortConfig.key === 'last' || sortConfig.key === 'joined') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return rows;
  }, [testerRows, sortConfig]);

  // --- Activity rows (Step 4) ---
  const activityRows = useMemo(() => {
    const rows = [];

    // Group events by chatRoomId for exchange rows
    const connectionMap = {};
    filteredEvents.forEach((e) => {
      const data = e.event_data || {};
      const chatRoomId = data.chatRoomId;
      if (!chatRoomId) {
        // Non-connection events (app_open) handled separately
        if (e.event_type === 'app_open') return; // processed below
        return;
      }

      if (!connectionMap[chatRoomId]) {
        connectionMap[chatRoomId] = { events: [], chatRoomId };
      }
      connectionMap[chatRoomId].events.push(e);
    });

    // Build exchange rows
    Object.values(connectionMap).forEach((conn) => {
      const { chatRoomId } = conn;
      const eventsByType = {};
      let radarLocation = null;
      let manualLocation = null;
      conn.events.forEach((e) => {
        if (e.event_type === 'connection_location') {
          const source = e.event_data?.source || 'radar';
          if (source === 'manual') {
            if (!manualLocation) manualLocation = e; // keep newest (first in desc order)
          } else {
            if (!radarLocation) radarLocation = e;
          }
        }
        if (!eventsByType[e.event_type]) eventsByType[e.event_type] = e;
      });

      const qrScanned = eventsByType['funnel_qr_scanned'];
      const manualAdd = eventsByType['manual_add'];

      // Manual add without QR scan — standalone manual connection row
      if (!qrScanned && manualAdd) {
        const profileUsername = manualAdd.event_data?.profileUsername;
        if (testerFilter && chatRoomToProfile[chatRoomId] !== testerFilter) return;

        // Manual add: derive name + contact types from connection_data
        const manualGuestData = guestDataMap[chatRoomId];

        rows.push({
          type: 'exchange',
          timestamp: manualAdd.created_at,
          chatRoomId,
          profileUsername,
          profileContactTypes: null,
          anonName: manualGuestData?.name || manualAdd.event_data?.contactName || null,
          anonContactTypes: manualGuestData?.contactTypes || null,
          anonSaved: false,
          profileChatCount: 0,
          anonChatCount: 0,
          profilePhotoCount: 0,
          anonPhotoCount: 0,
          hasInvite: false,
          isManualAdd: true,
          signedUp: false,
          profileHasNote: false,
          anonHasNote: false,
          venueName: radarLocation?.event_data?.venueName || null,
          city: radarLocation?.event_data?.city || null,
          manualVenueName: manualLocation?.event_data?.venueName || null,
          id: manualAdd.id,
        });
        return;
      }

      if (!qrScanned) return; // Skip orphans

      const profileUsername = qrScanned.event_data?.profileUsername;

      // Apply tester filter — match by profileUserId via chatRoomToProfile
      if (testerFilter && chatRoomToProfile[chatRoomId] !== testerFilter) return;

      const contactSaved = eventsByType['funnel_contact_saved'];
      const inviteClicked = eventsByType['funnel_invite_clicked'];
      const signupWaitlist = eventsByType['funnel_signup_waitlist'];
      const signupConfirmed = eventsByType['funnel_signup_confirmed'];
      const signupConverted = eventsByType['funnel_signup_converted'];
      const chatBySender = chatCountMap[chatRoomId] || {};
      const photoBySender = photoMap[chatRoomId] || {};
      const hasInvite = inviteSet.has(chatRoomId);
      // Guest data from connection_data (source of truth for all connection types)
      const guestData = guestDataMap[chatRoomId];

      // Host contact types from connection_data.sent_shared_fields
      const hostId = chatRoomToProfile[chatRoomId];
      const hostRow = connectionDataMap[chatRoomId]?.[hostId];
      const hostContactTypes = hostRow?.sent_shared_fields
        ? hostRow.sent_shared_fields.filter((f) => f !== 'photoUrl' && f !== 'name')
        : null;

      // Resolve host vs guest counts by identity, not sender role.
      // chatRoomToProfile gives the host's profile_user_id.
      const hostChatCount = hostId ? chatBySender[hostId] || 0 : 0;
      const guestChatCount = Object.entries(chatBySender)
        .filter(([sid]) => sid !== hostId)
        .reduce((sum, [, count]) => sum + count, 0);
      const hostPhotoCount = hostId ? photoBySender[hostId] || 0 : 0;
      const guestPhotoCount = Object.entries(photoBySender)
        .filter(([sid]) => sid !== hostId)
        .reduce((sum, [, count]) => sum + count, 0);

      // Notes: check which side wrote them
      const noteWriters = notesMap[chatRoomId];
      const profileHasNote = noteWriters ? noteWriters.has(hostId) : false;
      const anonHasNote = noteWriters ? [...noteWriters].some((id) => id !== hostId) : false;

      rows.push({
        type: 'exchange',
        timestamp: qrScanned.created_at,
        chatRoomId,
        profileUsername,
        profileContactTypes:
          hostContactTypes && hostContactTypes.length > 0 ? hostContactTypes : null,
        nameSharing: hostRow?.name_sharing || null,
        anonName: guestData?.name || null,
        anonContactTypes: guestData?.contactTypes || null,
        anonSaved: !!contactSaved,
        profileChatCount: hostChatCount,
        anonChatCount: guestChatCount,
        profilePhotoCount: hostPhotoCount,
        anonPhotoCount: guestPhotoCount,
        hasInvite,
        isManualAdd: !!manualAdd,
        profileHasNote,
        anonHasNote,
        inviteClicked:
          !!inviteClicked ||
          (!!signupWaitlist && signupWaitlist?.event_data?.source === 'invite_cta'),
        signedUp: !!signupWaitlist || !!signupConfirmed,
        signedUpViaWaitlist: !!signupWaitlist && !signupConfirmed,
        converted: !!signupConverted,
        signupSource:
          signupWaitlist?.event_data?.source || signupConfirmed?.event_data?.source || null,
        signupContactType: signupWaitlist?.event_data?.contactType || null,
        signupContactValue: signupWaitlist?.event_data?.contactValue || null,
        venueName: radarLocation?.event_data?.venueName || null,
        city: radarLocation?.event_data?.city || null,
        manualVenueName: manualLocation?.event_data?.venueName || null,
        id: qrScanned.id,
      });
    });

    // Browse rows disabled for Phase 1 launch
    // TODO: Re-enable once we distinguish "browse without connect" from "browse then connect"
    // const browseGroups = {};
    // filteredEvents.filter(e => e.event_type === 'app_open').forEach(...)
    // rows.push(...Object.values(browseGroups));

    // Standalone signup rows (welcome page signups with no chatRoomId)
    filteredEvents
      .filter((e) => e.event_type === 'funnel_signup_waitlist' && !e.event_data?.chatRoomId)
      .forEach((e) => {
        rows.push({
          type: 'standalone_signup',
          timestamp: e.created_at,
          chatRoomId: null,
          profileUsername: 'Welcome page',
          anonName: e.event_data.anonName,
          signedUp: true,
          signupContactType: e.event_data.contactType,
          signupContactValue: e.event_data.contactValue,
          signupSource: e.event_data.source,
          profileContactTypes: null,
          nameSharing: null,
          anonContactTypes: null,
          anonSaved: false,
          profileChatCount: 0,
          anonChatCount: 0,
          profilePhotoCount: 0,
          anonPhotoCount: 0,
          hasInvite: false,
          isManualAdd: false,
          profileHasNote: false,
          anonHasNote: false,
          inviteClicked: false,
          converted: false,
          venueName: null,
          city: null,
          manualVenueName: null,
          id: e.id,
        });
      });

    // Sort reverse-chronologically
    rows.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return rows;
  }, [
    filteredEvents,
    chatRoomToProfile,
    chatCountMap,
    photoMap,
    inviteSet,
    guestDataMap,
    connectionDataMap,
    testerFilter,
    notesMap,
  ]);

  // Apply activity type filter (e.g., "Signups" only shows rows with signedUp=true)
  const filteredActivityRows = useMemo(() => {
    if (activityTypeFilter === 'signups') {
      return activityRows.filter((row) => row.signedUp || row.converted);
    }
    return activityRows;
  }, [activityRows, activityTypeFilter]);

  // Tester options for filter dropdown (profileUserId → display label)
  const testerOptions = useMemo(() => {
    const nameCounts = {};
    testerRows.forEach((r) => {
      nameCounts[r.name] = (nameCounts[r.name] || 0) + 1;
    });
    return testerRows
      .map((r) => ({
        value: r.profileUserId,
        label: nameCounts[r.name] > 1 && r.phone ? `${r.name} (${r.phone.slice(-4)})` : r.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [testerRows]);

  // --- Handlers ---
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleTesterDrillDown = (profileUserId) => {
    setTesterFilter(profileUserId);
    setActiveView('activity');
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0 || demoMode) return;

    const ids = Array.from(selectedIds);
    try {
      const { error: deleteError } = await supabase.from('analytics_events').delete().in('id', ids);

      if (deleteError) {
        console.error('[Dashboard] Bulk delete error:', deleteError);
        return;
      }

      setSelectedIds(new Set());
      fetchEvents();
    } catch (err) {
      console.error('[Dashboard] Bulk delete exception:', err);
    }
  };

  // Delete a tester and all their data
  const handleDeleteTester = async (profileUserId) => {
    if (demoMode) return;

    // First click: show inline "Confirm?" state
    if (testerDeleteId !== profileUserId) {
      setTesterDeleteId(profileUserId);
      // Auto-reset after 3s if not confirmed
      setTimeout(() => setTesterDeleteId((prev) => (prev === profileUserId ? null : prev)), 3000);
      return;
    }

    // Second click: confirmed — execute delete
    setTesterDeleteId(null);

    try {
      // SECURITY DEFINER RPC — bypasses RLS for cross-user deletes
      const { error: delErr } = await supabase.rpc('dashboard_delete_user_data', {
        p_profile_user_id: profileUserId,
      });
      if (delErr) throw delErr;

      // Refresh dashboard
      fetchEvents();
      fetchProfilePhones();
      fetchPendingProfiles();
    } catch (err) {
      console.error('[Dashboard] Delete tester failed:', err);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // --- Utility ---
  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#F5F5F7',
        fontFamily: FONT_FAMILY,
        padding: SPACING.MD,
      }}
    >
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: SPACING.LG }}>
          <h1 style={{ ...TYPOGRAPHY.HEADER_LARGE, marginBottom: SPACING.SM }}>Royale Dashboard</h1>
          <p
            style={{
              ...TYPOGRAPHY.BODY_SMALL,
              color: demoMode ? '#FF9500' : COLORS.MEDIUM_GRAY,
              marginTop: SPACING.XS,
            }}
          >
            {loading
              ? 'Loading...'
              : demoMode
                ? `Demo Mode — ${events.length} sample events`
                : `${filteredEvents.length} events`}
            {error && <span style={{ color: '#FF3B30', marginLeft: '8px' }}>Error: {error}</span>}
          </p>
        </div>

        {/* Dashboard metrics — always visible */}
        <div
          style={{
            display: 'flex',
            gap: SPACING.SM,
            flexWrap: 'wrap',
            marginBottom: SPACING.MD,
          }}
        >
          {dashboardMetrics.map((m) => {
            const isProfile = m.role === 'profile';
            const cardBg = isProfile ? '#F7F7F9' : '#F0F6FF';
            const cardBorder = isProfile ? '#EDEDF0' : '#DCEAF9';
            const numberColor = isProfile ? '#1a1a1a' : COLORS.PRIMARY_BLUE;
            return (
              <div key={m.eventType} style={{ flex: 1, minWidth: '115px' }}>
                <div
                  style={{
                    backgroundColor: cardBg,
                    padding: SPACING.MD,
                    borderRadius: '8px',
                    border: `1px solid ${cardBorder}`,
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      ...TYPOGRAPHY.HEADER_LARGE,
                      color: numberColor,
                    }}
                  >
                    {m.display}
                    {m.suffix}
                  </div>
                  <div
                    style={{
                      ...TYPOGRAPHY.CAPTION_TEXT,
                      color: COLORS.MEDIUM_GRAY,
                      marginTop: '4px',
                    }}
                  >
                    {m.label}
                  </div>
                  {!m.isBaseline && (
                    <div style={{ fontSize: '11px', color: COLORS.MEDIUM_GRAY, marginTop: '2px' }}>
                      {m.count}
                    </div>
                  )}
                  {m.waitlistCount !== undefined && (
                    <div style={{ fontSize: '11px', color: COLORS.MEDIUM_GRAY, marginTop: '2px' }}>
                      ({m.waitlistCount} waitlist, {m.convertedCount} converted)
                    </div>
                  )}
                </div>
                {m.welcomeCount > 0 && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: COLORS.MEDIUM_GRAY,
                      textAlign: 'center',
                      marginTop: '4px',
                    }}
                  >
                    + {m.welcomeCount} welcome waitlist
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* View toggle — Testers / Activity */}
        <div
          style={{
            display: 'flex',
            gap: '0',
            marginBottom: SPACING.LG,
            backgroundColor: COLORS.WHITE,
            borderRadius: '10px',
            padding: '3px',
            border: `1px solid ${COLORS.LIGHT_GRAY}`,
            maxWidth: '360px',
          }}
        >
          {['testers', 'activity', 'timing'].map((view) => (
            <button
              key={view}
              onClick={() => {
                setActiveView(view);
                if (view !== 'activity') setTesterFilter(null);
              }}
              style={{
                flex: 1,
                padding: '8px 16px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: activeView === view ? '600' : '400',
                backgroundColor: activeView === view ? COLORS.PRIMARY_BLUE : 'transparent',
                color: activeView === view ? COLORS.WHITE : COLORS.MEDIUM_GRAY,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {view.charAt(0).toUpperCase() + view.slice(1)}
            </button>
          ))}
        </div>

        {/* Filters — hidden on timing view (has its own refresh) */}
        <div
          style={{
            backgroundColor: COLORS.WHITE,
            padding: '12px 16px',
            borderRadius: '12px',
            marginBottom: SPACING.LG,
            display: activeView === 'timing' ? 'none' : 'flex',
            alignItems: 'flex-end',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          {/* Time Filter */}
          <div style={{ flex: '0 0 auto' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '4px',
                color: COLORS.MEDIUM_GRAY,
                fontSize: '11px',
              }}
            >
              Time Period
            </label>
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: `1px solid ${COLORS.LIGHT_GRAY}`,
                fontSize: '14px',
                backgroundColor: COLORS.WHITE,
                width: '120px',
              }}
            >
              {Object.entries(TIME_FILTERS).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Branch Filter */}
          <div style={{ flex: '0 0 auto' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '4px',
                color: COLORS.MEDIUM_GRAY,
                fontSize: '11px',
              }}
            >
              Branch
            </label>
            <select
              value={branchFilter}
              onChange={(e) => {
                setBranchFilter(e.target.value);
                setSelectedIds(new Set());
              }}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: `1px solid ${COLORS.LIGHT_GRAY}`,
                fontSize: '14px',
                backgroundColor: COLORS.WHITE,
                width: '140px',
              }}
            >
              {availableBranches.map((branch) => (
                <option key={branch} value={branch}>
                  {branch === 'all' ? 'All branches' : branch}
                </option>
              ))}
            </select>
          </div>

          {/* Activity type filter (Activity view only) */}
          {activeView === 'activity' && (
            <div style={{ flex: '0 0 auto' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '4px',
                  color: COLORS.MEDIUM_GRAY,
                  fontSize: '11px',
                }}
              >
                Type
              </label>
              <select
                value={activityTypeFilter}
                onChange={(e) => setActivityTypeFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.LIGHT_GRAY}`,
                  fontSize: '14px',
                  backgroundColor: COLORS.WHITE,
                  width: '120px',
                }}
              >
                <option value="all">All</option>
                <option value="signups">Signups</option>
              </select>
            </div>
          )}

          {/* Tester filter (Activity view only) */}
          {activeView === 'activity' && (
            <div style={{ flex: '0 0 auto' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '4px',
                  color: COLORS.MEDIUM_GRAY,
                  fontSize: '11px',
                }}
              >
                Tester
              </label>
              <select
                value={testerFilter || 'all'}
                onChange={(e) => setTesterFilter(e.target.value === 'all' ? null : e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.LIGHT_GRAY}`,
                  fontSize: '14px',
                  backgroundColor: COLORS.WHITE,
                  width: '140px',
                }}
              >
                <option value="all">All testers</option>
                {testerOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ flex: 1 }} />

          {/* Error count link */}
          <a
            href="/admin/errors"
            style={{
              color: errorCount > 0 ? '#FF3B30' : COLORS.MEDIUM_GRAY,
              fontSize: '14px',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {errorCount > 0 ? `${errorCount} Error${errorCount !== 1 ? 's' : ''}` : ''}
          </a>

          {/* Refresh */}
          <button
            onClick={() => {
              fetchEvents(); // chat messages auto-refresh via events effect
              fetchErrorCount();
              fetchProfilePhones();
            }}
            disabled={loading}
            style={{
              padding: '8px 20px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: COLORS.PRIMARY_BLUE,
              color: COLORS.WHITE,
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        {/* === TESTERS VIEW === */}
        {activeView === 'testers' && (
          <>
            <div
              style={{
                backgroundColor: COLORS.WHITE,
                borderRadius: '12px',
                overflow: 'auto',
                border: `1px solid ${COLORS.LIGHT_GRAY}`,
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                }}
              >
                <thead>
                  <tr>
                    {[
                      { key: 'name', label: 'Name' },
                      { key: 'phone', label: 'Phone' },
                      { key: 'last', label: 'Last' },
                      { key: 'lastOpened', label: 'Opened' },
                      { key: 'connects', label: 'Connects' },
                      { key: 'sharedPct', label: 'Shared' },
                      { key: 'savedPct', label: 'Saved' },
                      { key: 'chatPct', label: 'Chat' },
                      { key: 'invited', label: 'Invited' },
                      { key: 'signups', label: 'Signed up' },
                      { key: 'manual', label: 'Manual' },
                      // { key: 'browsed', label: 'Browsed' }, // Browse tracking disabled for Phase 1 launch
                      { key: 'joined', label: 'Joined' },
                      { key: 'delete', label: '' },
                    ].map((col) => (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        style={{
                          padding: '10px 8px',
                          textAlign: col.key === 'name' || col.key === 'phone' ? 'left' : 'center',
                          borderBottom: `1px solid ${COLORS.LIGHT_GRAY}`,
                          color:
                            sortConfig.key === col.key ? COLORS.PRIMARY_BLUE : COLORS.MEDIUM_GRAY,
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          userSelect: 'none',
                        }}
                      >
                        {col.label}
                        {sortConfig.key === col.key &&
                          (sortConfig.direction === 'asc' ? ' ↑' : ' ↓')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedTesterRows.map((row) => (
                    <tr
                      key={row.profileUserId}
                      style={{ borderBottom: `1px solid ${COLORS.LIGHT_GRAY}` }}
                    >
                      <td
                        onClick={() => handleTesterDrillDown(row.profileUserId)}
                        style={{
                          padding: '10px 8px',
                          fontWeight: '500',
                          color: COLORS.PRIMARY_BLUE,
                          cursor: 'pointer',
                        }}
                      >
                        {row.name}
                        {row.branch && (
                          <div style={{ fontSize: '10px', color: '#999', fontWeight: '400' }}>
                            {row.branch}
                          </div>
                        )}
                      </td>
                      <td
                        style={{
                          padding: '10px 8px',
                          textAlign: 'left',
                          color: '#666',
                          fontSize: '12px',
                        }}
                      >
                        {row.phone || '—'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center', color: '#666' }}>
                        {formatDate(row.last)}
                      </td>
                      <td
                        style={{
                          padding: '10px 8px',
                          textAlign: 'center',
                          color: row.lastOpened ? '#007AFF' : '#999',
                        }}
                      >
                        {row.lastOpened ? formatDate(row.lastOpened) : '—'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: '600' }}>
                        {row.connects}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>{row.sharedPct}%</td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>{row.savedPct}%</td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>{row.chatPct}%</td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                        {row.invited || '—'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                        {row.signups + row.conversions || '—'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                        {row.manual || '—'}
                      </td>
                      {/* Browse tracking disabled for Phase 1 launch */}
                      {/* <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                      {row.browsed || '—'}
                    </td> */}
                      <td style={{ padding: '10px 8px', textAlign: 'center', color: '#666' }}>
                        {formatDate(row.joined)}
                      </td>
                      <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleDeleteTester(row.profileUserId)}
                          style={{
                            background: testerDeleteId === row.profileUserId ? '#FF3B30' : 'none',
                            border:
                              testerDeleteId === row.profileUserId ? '1px solid #FF3B30' : 'none',
                            color: testerDeleteId === row.profileUserId ? '#FFFFFF' : '#FF3B30',
                            fontSize: '12px',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontWeight: testerDeleteId === row.profileUserId ? '600' : '400',
                          }}
                        >
                          {testerDeleteId === row.profileUserId ? 'Confirm?' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {sortedTesterRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={11}
                        style={{
                          padding: '40px',
                          textAlign: 'center',
                          color: COLORS.MEDIUM_GRAY,
                        }}
                      >
                        No tester data yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pending Profiles — people who confirmed phone but haven't completed OTP */}
            <div
              style={{
                marginTop: '32px',
                border: `1px solid ${COLORS.LIGHT_GRAY}`,
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '12px 16px',
                  borderBottom: `1px solid ${COLORS.LIGHT_GRAY}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: COLORS.DARK_GRAY,
                  }}
                >
                  Pending Profiles
                </span>
                <span
                  style={{
                    fontSize: '12px',
                    color: COLORS.MEDIUM_GRAY,
                  }}
                >
                  {pendingProfiles.length} pending
                </span>
              </div>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px',
                }}
              >
                <thead>
                  <tr>
                    {['Anon', 'Connected With', 'Phone', 'Created', 'Expires', ''].map((label) => (
                      <th
                        key={label || 'delete'}
                        style={{
                          padding: '10px 8px',
                          textAlign: 'left',
                          borderBottom: `1px solid ${COLORS.LIGHT_GRAY}`,
                          color: COLORS.MEDIUM_GRAY,
                          fontWeight: '600',
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pendingProfiles.map((p) => (
                    <tr
                      key={p.id}
                      style={{
                        borderBottom: `1px solid ${COLORS.LIGHT_GRAY}`,
                      }}
                    >
                      <td style={{ padding: '10px 8px', fontWeight: '500' }}>
                        {p.anonName || '—'}
                      </td>
                      <td style={{ padding: '10px 8px', color: '#666' }}>
                        {p.profileUserName || '—'}
                      </td>
                      <td style={{ padding: '10px 8px', color: '#666' }}>{p.phone}</td>
                      <td style={{ padding: '10px 8px', color: '#666' }}>
                        {formatDate(p.created_at)}
                      </td>
                      <td style={{ padding: '10px 8px', color: '#666' }}>
                        {formatDate(p.expires_at)}
                      </td>
                      <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleDeletePending(p)}
                          style={{
                            background: pendingDeleteId === p.id ? '#FF3B30' : 'none',
                            border: pendingDeleteId === p.id ? '1px solid #FF3B30' : 'none',
                            color: pendingDeleteId === p.id ? '#FFFFFF' : '#FF3B30',
                            fontSize: '12px',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontWeight: pendingDeleteId === p.id ? '600' : '400',
                          }}
                        >
                          {pendingDeleteId === p.id ? 'Confirm?' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {pendingProfiles.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          padding: '24px',
                          textAlign: 'center',
                          color: COLORS.MEDIUM_GRAY,
                        }}
                      >
                        No pending profiles
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* === ACTIVITY VIEW === */}
        {activeView === 'activity' && (
          <div>
            {/* Select all / Delete controls */}
            {filteredActivityRows.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: SPACING.SM,
                }}
              >
                {branchFilter !== 'main' && branchFilter !== 'all' && (
                  <button
                    onClick={() => {
                      if (selectedIds.size === activityRows.length) {
                        setSelectedIds(new Set());
                      } else {
                        setSelectedIds(new Set(filteredActivityRows.map((r) => r.id)));
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 10px',
                      border: `1px solid ${COLORS.LIGHT_GRAY}`,
                      borderRadius: '6px',
                      backgroundColor: COLORS.WHITE,
                      fontSize: '12px',
                      color: COLORS.MEDIUM_GRAY,
                      cursor: 'pointer',
                    }}
                  >
                    {selectedIds.size === filteredActivityRows.length ? (
                      <CheckSquare size={14} weight="fill" color={COLORS.PRIMARY_BLUE} />
                    ) : (
                      <Square size={14} color="#ccc" />
                    )}
                    {selectedIds.size === filteredActivityRows.length
                      ? 'Deselect all'
                      : 'Select all'}
                  </button>
                )}
                {selectedIds.size > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: '#FF3B30',
                      color: COLORS.WHITE,
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Trash size={14} weight="bold" />
                    Delete {selectedIds.size}
                  </button>
                )}
              </div>
            )}

            {/* Tester filter indicator */}
            {testerFilter && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: SPACING.SM,
                  padding: '8px 12px',
                  backgroundColor: '#EBF5FF',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              >
                <span>
                  Filtered to{' '}
                  <strong>
                    {testerOptions.find((o) => o.value === testerFilter)?.label || testerFilter}
                  </strong>
                </span>
                <button
                  onClick={() => setTesterFilter(null)}
                  style={{
                    border: 'none',
                    background: 'none',
                    color: COLORS.PRIMARY_BLUE,
                    cursor: 'pointer',
                    fontSize: '13px',
                    padding: '2px 6px',
                  }}
                >
                  Clear
                </button>
              </div>
            )}

            {filteredActivityRows.slice(0, activityLimit).map((row) => {
              const profileDisplayName =
                row.nameSharing === 'first'
                  ? row.profileUsername?.split(' ')[0]
                  : row.profileUsername;

              return (
                <div
                  key={row.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr 1fr 100px 60px' : '1fr 1fr 180px 80px',
                    columnGap: isMobile ? '10px' : '16px',
                    alignItems: 'center',
                    backgroundColor: COLORS.WHITE,
                    borderRadius: '10px',
                    padding: isMobile ? '10px 10px' : '10px 14px',
                    marginBottom: '6px',
                    border: `1px solid ${COLORS.LIGHT_GRAY}`,
                  }}
                >
                  {/* Column 1: Host */}
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontSize: isMobile ? '13px' : '14px',
                        marginBottom: '4px',
                        flexWrap: 'wrap',
                      }}
                    >
                      {!isMobile && (
                        <div
                          onClick={() => toggleSelect(row.id)}
                          style={{ cursor: 'pointer', flexShrink: 0 }}
                        >
                          {selectedIds.has(row.id) ? (
                            <CheckSquare size={16} weight="fill" color={COLORS.PRIMARY_BLUE} />
                          ) : (
                            <Square size={16} color="#ccc" />
                          )}
                        </div>
                      )}
                      <span style={{ fontWeight: '600' }}>{profileDisplayName}</span>
                      {row.profileChatCount > 0 && <ActivityIcon icon={ChatCircle} bg="#7B61FF" />}
                      {row.profilePhotoCount > 0 && <ActivityIcon icon={Camera} bg="#7B61FF" />}
                      {row.profileHasNote && (
                        <ActivityIcon icon={NoteIcon} bg="#C7C7CC" color="#444" />
                      )}
                      {row.hasInvite && <ActivityIcon icon={GiftIcon} bg="#1a1a1a" />}
                      {row.isManualAdd && <Pill label="Manual" color="#8B7B2A" textColor="#fff" />}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        flexWrap: 'wrap',
                      }}
                    >
                      {row.profileContactTypes?.map((ct) => (
                        <ContactTypeIcon key={ct} type={ct} variant="profile" />
                      ))}
                    </div>
                  </div>

                  {/* Column 2: Guest */}
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontSize: isMobile ? '13px' : '14px',
                        marginBottom: '4px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ fontWeight: '600' }}>{row.anonName || '—'}</span>
                      {row.anonChatCount > 0 && <ActivityIcon icon={ChatCircle} bg="#7B61FF" />}
                      {row.anonPhotoCount > 0 && <ActivityIcon icon={Camera} bg="#7B61FF" />}
                      {row.anonHasNote && (
                        <ActivityIcon icon={NoteIcon} bg="#C7C7CC" color="#444" />
                      )}
                      {row.inviteClicked && <ActivityIcon icon={GiftIcon} bg="#1a1a1a" />}
                      {(row.signedUp || row.converted) && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 22,
                            height: 22,
                            borderRadius: 6,
                            backgroundColor: '#34C759',
                            color: '#fff',
                            fontSize: '13px',
                            fontWeight: '700',
                            flexShrink: 0,
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        flexWrap: 'wrap',
                      }}
                    >
                      {row.anonContactTypes?.map((ct) => (
                        <ContactTypeIcon key={ct} type={ct} variant="anon" />
                      ))}
                      {row.anonSaved && (
                        <ActivityIcon icon={FileArrowDown} bg="#D4E4FF" color="#007AFF" />
                      )}
                      {row.signupContactValue && (
                        <span style={{ color: '#666', fontSize: '12px', marginLeft: '4px' }}>
                          {row.signupContactValue}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Column 3: Location */}
                  <div
                    style={{
                      color: '#999',
                      fontSize: '12px',
                      whiteSpace: isMobile ? 'normal' : 'nowrap',
                    }}
                  >
                    <div>
                      {row.venueName
                        ? `${row.venueName}${row.city ? `, ${row.city}` : ''}`
                        : row.city || ''}
                    </div>
                    {row.manualVenueName && (
                      <div style={{ color: '#bbb', marginTop: '1px' }}>({row.manualVenueName})</div>
                    )}
                  </div>

                  {/* Column 4: Date/Time */}
                  <div
                    style={{
                      color: '#999',
                      fontSize: isMobile ? '11px' : '12px',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <div>{formatDate(row.timestamp)}</div>
                    <div>{formatTime(row.timestamp)}</div>
                  </div>
                </div>
              );
            })}

            {filteredActivityRows.length > activityLimit && (
              <button
                onClick={() => setActivityLimit((prev) => prev + 100)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${COLORS.LIGHT_GRAY}`,
                  borderRadius: '10px',
                  backgroundColor: COLORS.WHITE,
                  color: COLORS.PRIMARY_BLUE,
                  fontSize: '14px',
                  cursor: 'pointer',
                  marginTop: '6px',
                }}
              >
                Load more ({filteredActivityRows.length - activityLimit} remaining)
              </button>
            )}

            {filteredActivityRows.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: COLORS.MEDIUM_GRAY,
                }}
              >
                No activity yet
              </div>
            )}
          </div>
        )}

        {activeView === 'timing' && (
          <TimingView rows={timingRows} loading={timingLoading} onRefresh={fetchTimingData} />
        )}
      </div>
    </div>
  );
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

// ============================================================
// TIMING VIEW
// ============================================================

const TIMING_COLOR = (ms) => {
  if (ms == null) return '#999';
  if (ms < 1000) return '#34C759'; // green
  if (ms < 3000) return '#FF9500'; // yellow/orange
  return '#FF3B30'; // red
};

// Plain English labels for milestone variable names
// key = true means this is a meaningful milestone (bold in UI)
const MILESTONE_META = {
  // Browser navigation (blank screen) — all times from QR tap (T0 = navigation start)
  ttfb: { label: 'Server response', key: true },
  html_downloaded: { label: 'HTML downloaded' },
  dom_ready: { label: 'DOM ready' },
  spinner_shown: { label: 'Spinner visible', key: true },
  // Data prefetch (runs parallel with JS download)
  prefetch: { label: 'Data ready', key: true, isDuration: true },
  // React lifecycle
  react_mount: { label: 'React mount', key: true },
  ready: { label: 'Content visible', key: true },
  // Connection flow
  init_connection: { label: null },
  db_write: { label: 'DB confirmed', key: true },
  ws_join_start: { label: null },
  ws_join_complete: { label: 'Chat ready', key: true },
  confirmed: { label: 'Contact received' },
  // Profile side
  anon_detected: { label: 'Anon detected', key: true },
  confirmation_sent: { label: 'Confirmation sent', key: true },
  chat_page_mounted: { label: 'Chat ready', key: true },
  people_landed: { label: 'Landed on People', key: true },
  // Nearby (proximity) connects — T0 = the QR screen opened and asked for GPS
  gps_ready: { label: 'GPS fix', key: true },
  nearby_started: { label: 'Nearby: looking' },
  nearby_woke: { label: 'Nearby: back from lock' },
  nearby_back: { label: 'Nearby: first check-in back' },
  nearby_seen: { label: 'Nearby: person shown', key: true },
  nearby_tap: { label: 'Nearby: Connect tapped' },
  nearby_matched: { label: 'Nearby: matched', key: true },
};

const TimingView = ({ rows, loading, onRefresh }) => {
  const [selectedTimingIds, setSelectedTimingIds] = useState(new Set());

  // Group rows by chat_room_id
  const grouped = useMemo(() => {
    const groups = {};
    rows.forEach((row) => {
      const key = row.chat_room_id || row.id;
      if (!groups[key]) groups[key] = {};
      groups[key][row.user_type] = row;
    });
    return Object.entries(groups)
      .sort(([, a], [, b]) => {
        const tsA = new Date(a.anon?.created_at || a.profile?.created_at);
        const tsB = new Date(b.anon?.created_at || b.profile?.created_at);
        return tsB - tsA;
      })
      .map(([id, sides]) => ({ chatRoomId: id, sides }));
  }, [rows]);

  // Summary: anon total_ms is now QR tap → chat ready (the full user experience)
  const summary = useMemo(() => {
    const combinedTotals = [];
    const anonTotals = [];
    const profileTotals = [];
    const ttfbTotals = [];
    grouped.forEach(({ sides }) => {
      const aMs = sides.anon?.total_ms;
      const pMs = sides.profile?.total_ms;
      if (aMs != null) anonTotals.push(aMs);
      if (pMs != null) profileTotals.push(pMs);
      if (aMs != null || pMs != null) {
        combinedTotals.push((aMs || 0) + (pMs || 0));
      }
      // TTFB from milestones (blank screen duration)
      const ttfb = sides.anon?.milestones?.ttfb;
      if (ttfb != null) ttfbTotals.push(typeof ttfb === 'object' ? ttfb.ms : ttfb);
    });
    const avg = (arr) =>
      arr.length ? Math.round(arr.reduce((s, v) => s + v, 0) / arr.length) : null;
    return {
      combinedAvg: avg(combinedTotals),
      combinedCount: combinedTotals.length,
      anonAvg: avg(anonTotals),
      profileAvg: avg(profileTotals),
      ttfbAvg: avg(ttfbTotals),
      ttfbCount: ttfbTotals.length,
    };
  }, [grouped]);

  // Detection method counts (profile rows only)
  const detectionCounts = useMemo(() => {
    const counts = {};
    rows.forEach((r) => {
      if (r.anon_detection_method) {
        counts[r.anon_detection_method] = (counts[r.anon_detection_method] || 0) + 1;
      }
    });
    return counts;
  }, [rows]);

  const toggleTimingSelect = (chatRoomId) => {
    setSelectedTimingIds((prev) => {
      const next = new Set(prev);
      if (next.has(chatRoomId)) next.delete(chatRoomId);
      else next.add(chatRoomId);
      return next;
    });
  };

  const handleTimingBulkDelete = async () => {
    if (selectedTimingIds.size === 0) return;
    // Get all row IDs for selected chat_room_ids
    const idsToDelete = rows
      .filter((r) => selectedTimingIds.has(r.chat_room_id || r.id))
      .map((r) => r.id);
    try {
      const { error: deleteError } = await supabase
        .from('connection_timings')
        .delete()
        .in('id', idsToDelete);
      if (deleteError) {
        console.error('[Dashboard] Timing delete error:', deleteError);
        return;
      }
      setSelectedTimingIds(new Set());
      onRefresh();
    } catch (err) {
      console.error('[Dashboard] Timing delete exception:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: COLORS.MEDIUM_GRAY }}>
        Loading timing data...
      </div>
    );
  }

  return (
    <div>
      {/* Summary row */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: SPACING.LG,
          flexWrap: 'wrap',
        }}
      >
        <SummaryCard
          label="TTFB avg"
          value={summary.ttfbAvg != null ? `${summary.ttfbAvg}ms` : '—'}
          sub={summary.ttfbCount > 0 ? `${summary.ttfbCount} scans` : 'No data yet'}
          color={TIMING_COLOR(summary.ttfbAvg)}
        />
        <SummaryCard
          label="Anon avg"
          value={summary.anonAvg != null ? `${summary.anonAvg}ms` : '—'}
          sub="QR tap → chat ready"
          color={TIMING_COLOR(summary.anonAvg)}
        />
        <SummaryCard
          label="Profile avg"
          value={summary.profileAvg != null ? `${summary.profileAvg}ms` : '—'}
          sub={`${summary.combinedCount} connections`}
          color={TIMING_COLOR(summary.profileAvg)}
        />
        {Object.keys(detectionCounts).length > 0 && (
          <div
            style={{
              flex: '1 1 140px',
              backgroundColor: COLORS.WHITE,
              borderRadius: '10px',
              padding: '12px 16px',
              border: `1px solid ${COLORS.LIGHT_GRAY}`,
            }}
          >
            <div style={{ fontSize: '11px', color: COLORS.MEDIUM_GRAY, marginBottom: '4px' }}>
              Detection methods
            </div>
            {Object.entries(detectionCounts).map(([method, count]) => (
              <div key={method} style={{ fontSize: '13px' }}>
                <strong>{count}</strong> <span style={{ color: COLORS.MEDIUM_GRAY }}>{method}</span>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={onRefresh}
          style={{
            alignSelf: 'flex-start',
            padding: '8px 14px',
            border: `1px solid ${COLORS.LIGHT_GRAY}`,
            borderRadius: '8px',
            backgroundColor: COLORS.WHITE,
            fontSize: '13px',
            color: COLORS.PRIMARY_BLUE,
            cursor: 'pointer',
          }}
        >
          Refresh
        </button>
      </div>

      {/* Select all / Delete controls */}
      {grouped.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: SPACING.SM,
          }}
        >
          <button
            onClick={() => {
              if (selectedTimingIds.size === grouped.length) {
                setSelectedTimingIds(new Set());
              } else {
                setSelectedTimingIds(new Set(grouped.map((g) => g.chatRoomId)));
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 10px',
              border: `1px solid ${COLORS.LIGHT_GRAY}`,
              borderRadius: '6px',
              backgroundColor: COLORS.WHITE,
              fontSize: '12px',
              color: COLORS.MEDIUM_GRAY,
              cursor: 'pointer',
            }}
          >
            {selectedTimingIds.size === grouped.length ? (
              <CheckSquare size={14} weight="fill" color={COLORS.PRIMARY_BLUE} />
            ) : (
              <Square size={14} color="#ccc" />
            )}
            {selectedTimingIds.size === grouped.length ? 'Deselect all' : 'Select all'}
          </button>
          {selectedTimingIds.size > 0 && (
            <button
              onClick={handleTimingBulkDelete}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#FF3B30',
                color: COLORS.WHITE,
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Trash size={14} weight="bold" />
              Delete {selectedTimingIds.size}
            </button>
          )}
        </div>
      )}

      {/* Connections table — grouped by chat_room_id */}
      {grouped.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: COLORS.MEDIUM_GRAY }}>
          No timing data yet. Make a connection to start capturing.
        </div>
      ) : (
        grouped.map(({ chatRoomId: roomId, sides }) => (
          <TimingRow
            key={roomId}
            chatRoomId={roomId}
            sides={sides}
            selected={selectedTimingIds.has(roomId)}
            onToggleSelect={() => toggleTimingSelect(roomId)}
          />
        ))
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, sub, color }) => (
  <div
    style={{
      flex: '1 1 120px',
      backgroundColor: COLORS.WHITE,
      borderRadius: '10px',
      padding: '12px 16px',
      border: `1px solid ${COLORS.LIGHT_GRAY}`,
    }}
  >
    <div style={{ fontSize: '11px', color: COLORS.MEDIUM_GRAY, marginBottom: '2px' }}>{label}</div>
    <div style={{ fontSize: '22px', fontWeight: '600', color }}>{value}</div>
    <div style={{ fontSize: '11px', color: COLORS.MEDIUM_GRAY }}>{sub}</div>
  </div>
);

const TimingRow = ({ chatRoomId, sides, selected, onToggleSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const anon = sides.anon;
  const profile = sides.profile;
  const primary = anon || profile;
  const ts = new Date(primary.created_at);
  const timeStr = ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const dateStr = ts.toLocaleDateString([], { month: 'short', day: 'numeric' });
  // Anon total_ms is now QR tap → chat ready (full user experience)
  const anonMs = anon?.total_ms;
  const profileMs = profile?.total_ms;
  // Extract milestone values (may be plain number or { ms: N })
  const getMsVal = (m) => (m != null ? (typeof m === 'object' ? m.ms : m) : null);
  const ttfbVal = getMsVal(anon?.milestones?.ttfb);
  // Spinner duration: spinner_shown → ready (how long user stares at spinner)
  const spinnerShown = getMsVal(anon?.milestones?.spinner_shown);
  const readyMs = getMsVal(anon?.milestones?.ready);
  const spinnerDuration = spinnerShown != null && readyMs != null ? readyMs - spinnerShown : null;

  return (
    <div
      style={{
        backgroundColor: COLORS.WHITE,
        borderRadius: '10px',
        padding: '10px 14px',
        marginBottom: '6px',
        border: `1px solid ${selected ? COLORS.PRIMARY_BLUE : COLORS.LIGHT_GRAY}`,
        cursor: 'pointer',
      }}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect();
          }}
          style={{ cursor: 'pointer', flexShrink: 0 }}
        >
          {selected ? (
            <CheckSquare size={16} weight="fill" color={COLORS.PRIMARY_BLUE} />
          ) : (
            <Square size={16} color="#ccc" />
          )}
        </span>
        {/* Key metrics: anon total (QR tap → chat ready), TTFB, profile */}
        <span style={{ fontWeight: '700', fontSize: '15px', color: TIMING_COLOR(anonMs) }}>
          {anonMs != null ? `${anonMs}ms` : '—'}
        </span>
        {ttfbVal != null && (
          <>
            <span style={{ color: '#ddd', fontSize: '13px', margin: '0 2px' }}>|</span>
            <span style={{ fontSize: '12px', color: TIMING_COLOR(ttfbVal) }}>{ttfbVal}ms ttfb</span>
          </>
        )}
        {spinnerDuration != null && (
          <>
            <span style={{ color: '#ddd', fontSize: '13px', margin: '0 2px' }}>|</span>
            <span style={{ fontSize: '12px', color: TIMING_COLOR(spinnerDuration) }}>
              {spinnerDuration}ms spinner
            </span>
          </>
        )}
        <span style={{ color: '#ddd', fontSize: '13px', margin: '0 2px' }}>|</span>
        <span style={{ fontWeight: '600', fontSize: '13px', color: TIMING_COLOR(profileMs) }}>
          {profileMs != null ? `${profileMs}ms` : '—'}
        </span>
        <span style={{ color: '#bbb', fontSize: '11px', marginLeft: '4px' }}>
          ...{chatRoomId?.slice(-6)}
        </span>
        {(anon?.proximity_active || profile?.proximity_active) && (
          <span style={{ fontSize: '10px', color: '#FF9500', fontWeight: '500' }}>PROX</span>
        )}
        {profile?.anon_detection_method && (
          <span style={{ fontSize: '11px', color: COLORS.MEDIUM_GRAY }}>
            {profile.anon_detection_method}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {(profile?.user_label || anon?.user_label) && (
          <span style={{ color: '#666', fontSize: '12px' }}>
            {profile?.user_label || anon?.user_label}
          </span>
        )}
        <span style={{ color: '#999', fontSize: '12px' }}>
          {dateStr} {timeStr}
        </span>
        <span style={{ color: '#ccc', fontSize: '10px' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {/* Expanded milestones — show both sides */}
      {expanded && (
        <div
          style={{
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: `1px solid ${COLORS.LIGHT_GRAY}`,
            fontSize: '12px',
            fontFamily: FONT_FAMILY,
          }}
        >
          {[
            { label: 'Anon', data: anon },
            { label: 'Profile', data: profile },
          ]
            .filter((s) => s.data?.milestones)
            .map((side) => (
              <div key={side.label} style={{ marginBottom: '12px' }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#333',
                    marginBottom: '4px',
                    textAlign: 'left',
                  }}
                >
                  {side.label}{' '}
                  <span style={{ color: TIMING_COLOR(side.data.total_ms) }}>
                    {side.data.total_ms != null ? `${side.data.total_ms}ms` : '—'}
                  </span>
                </div>
                {Object.entries(side.data.milestones)
                  .sort((a, b) => {
                    const msA = typeof a[1] === 'object' ? a[1].ms : a[1];
                    const msB = typeof b[1] === 'object' ? b[1].ms : b[1];
                    return msA - msB;
                  })
                  .map(([name, value]) => {
                    const meta = MILESTONE_META[name] || {};
                    const ms = typeof value === 'object' ? value.ms : value;
                    // Show duration as extra info (e.g., prefetch query time)
                    const extra =
                      typeof value === 'object'
                        ? Object.entries(value)
                            .filter(([k]) => k !== 'ms')
                            .map(([k, v]) => `${k}=${v}`)
                            .join(' ')
                        : null;
                    const isKey = meta.key;
                    const displayLabel = meta.label;
                    return (
                      <div
                        key={name}
                        style={{
                          display: 'flex',
                          gap: '8px',
                          padding: isKey ? '3px 0' : '2px 0',
                          color: COLORS.MEDIUM_GRAY,
                          marginTop: isKey && !meta._prevKey ? '4px' : 0,
                        }}
                      >
                        <span
                          style={{
                            minWidth: '55px',
                            textAlign: 'right',
                            color: TIMING_COLOR(ms),
                            fontWeight: isKey ? '600' : '400',
                          }}
                        >
                          {ms}ms
                        </span>
                        {isKey ? (
                          <>
                            <span style={{ fontWeight: '700', color: '#333' }}>{displayLabel}</span>
                            <span style={{ color: '#bbb', fontSize: '11px' }}>({name})</span>
                          </>
                        ) : (
                          <span style={{ color: '#999' }}>{name}</span>
                        )}
                        {extra && <span style={{ color: '#aaa', fontSize: '11px' }}>{extra}</span>}
                      </div>
                    );
                  })}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

const Pill = ({ label, color, textColor }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: '500',
      backgroundColor: color,
      color: textColor,
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </span>
);

// Icon in a rounded-rect background — used for activity indicators and contact type icons
const ActivityIcon = ({ icon: Icon, bg, color = '#fff', size = 13, badge }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 22,
      height: 22,
      borderRadius: 6,
      backgroundColor: bg,
      flexShrink: 0,
      position: 'relative',
    }}
  >
    <Icon size={size} weight="bold" color={color} />
    {badge && (
      <span
        style={{
          position: 'absolute',
          top: -3,
          right: -5,
          fontSize: '8px',
          fontWeight: '700',
          color: '#666',
        }}
      >
        {badge}
      </span>
    )}
  </span>
);

// Map contact type string → { icon, badge } for ContactTypeIcon rendering
const CONTACT_TYPE_ICONS = {
  phone: { icon: PhoneIcon },
  phone2: { icon: PhoneIcon, badge: '2' },
  email: { icon: EnvelopeSimple },
  email2: { icon: EnvelopeSimple, badge: '2' },
  instagram: { icon: InstagramLogo },
  instagram2: { icon: InstagramLogo, badge: '2' },
  whatsapp: { icon: WhatsappLogo },
  whatsapp2: { icon: WhatsappLogo, badge: '2' },
  linkedin: { icon: LinkedinLogo },
  facebook: { icon: FacebookLogo },
  github: { icon: GithubLogo },
  telegram: { icon: TelegramLogo },
  twitter: { icon: XLogo },
  x: { icon: XLogo },
  youtube: { icon: YoutubeLogo },
  signal: { icon: ChatCircle },
  aboutMe: { icon: UserIcon },
  aboutme: { icon: UserIcon },
  other: { icon: DotsThree },
};

// Render a contact type as a small icon in a rounded rect
const ContactTypeIcon = ({ type, variant = 'profile' }) => {
  const entry = CONTACT_TYPE_ICONS[type] || CONTACT_TYPE_ICONS.other;
  const bg = variant === 'anon' ? '#D4E4FF' : '#E5E5EA';
  const color = variant === 'anon' ? '#007AFF' : '#666';
  return <ActivityIcon icon={entry.icon} bg={bg} color={color} badge={entry.badge} />;
};

export default AnalyticsDashboard;
